import React from 'react';
import * as _ from 'lodash';

import JobCategoryApi from '../services/jobCategory/JobCategoryApi';
import PositionApi from '../services/position/PositionApi';
import FieldTreeWorkersApi from '../services/fieldTreeWorkers/FieldTreeWorkersApi';
import FieldTreeWorkersService from '../services/fieldTreeWorkers/FieldTreeWorkersService';

import { PositionDropdown } from './dropdowns/single/Position';
import { JobCategoryDropdown } from './dropdowns/single/JobCategory';
import { Information } from './modals/common/';

import { isDeletedFieldTreeError } from '../utilities/apiErrorMessage';
import { extractErrorMessage } from '../utilities/commonUtil';
import { messageUtility } from '../utilities/messageUtility';
import { isNullOrUndefinedOrEmptyStr } from '../utilities/stringUtil';
import {
  SORT_TYPE,
  WORKER_STATUS,
  CCUS_JOB_CODE,
} from '../utilities/constants';
import Joi from 'joi-browser';
import regex from '../utilities/validateRegex';
import { validationByJoi } from '../utilities/commonValidation';

import Styles from '../styles/componentStyle/FieldTreeJobAndPositionRegisterDialog/FieldTreeJobAndPositionRegisterDialog.scss';

/**
 * 施工体系図 > 作業員職種・立場登録ダイアログ
 */
export default class FieldTreeJobAndPositionRegisterDialog extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      jobCategoriesList: [], // 職種マスター
      positionsList: [], // 立場マスター
      assignedWorkerList: [], //現場に登録中の作業員のリスト (実際に画面に表示したり、値を送信するデータ)
      verifyAssignedWorkerList: [], //現場に登録中の作業員のリスト (変更されたかどうかをチェックするためのデータ)
      fetchAssignedWorkerParam: {
        // 現場に割り当てられているユーザー情報を取得する時のパラメーター
        worker_status: [WORKER_STATUS.IN_OPERATION.ID], // 『稼働中』データのみ取得する
        order: '', // ソート項目
        sortType: '',
      },
      informationModal: {
        // エラーが発生した時に表示するダイアログ
        show: false,
        message: '',
      },
      isDeletedFieldTree: false, // 協力会社作業が削除されたフラグ
    };

    // 現場IDが存在するか確認する
    if (_.isUndefined(props.dialog.state.field_id)) {
      throw new Error(
        'FieldTreeJobAndPositionRegisterDialog.jsx constructor props.field_id'
      );
    }

    // 施工体系IDが存在するか確認する
    if (_.isUndefined(props.dialog.state.field_tree_id)) {
      throw new Error(
        'FieldTreeJobAndPositionRegisterDialog.jsx constructor props.field_tree_id'
      );
    }

    //入力データの規則を定義
    this.scheme = Joi.object().keys({
      // 作業内容
      // 全角（特殊文字と英数値を禁止）
      // 最大100文字
      // ブランクを許可
      work_content: Joi.string()
        .regex(
          regex.pattern
            .otherThanZenkakuMarkAndEnglishAndNumericAndHankakuKatakana,
          {
            name: 'otherThanZenkakuMarkAndEnglishAndNumericAndHankakuKatakana',
          }
        )
        .max(100)
        .allow(null, ''),
    });

    this.scrollFrameRef = React.createRef();
  }

  /**
   * React Component LifeCycle
   * マウントが行われた直後
   * */
  async componentDidMount() {
    const stateValue = _.cloneDeep(this.state);

    // 職種マスターを取得
    await JobCategoryApi.fetchJobCategoriesList()
      .then(response => {
        if (response.result === true) {
          stateValue.jobCategoriesList = response.data.entities.ginou_syokusyus;
        } else {
          stateValue.informationModal.show = true;
          stateValue.informationModal.message = extractErrorMessage(response);
        }
      })
      .catch(error => {
        stateValue.informationModal.show = true;
        stateValue.informationModal.message = extractErrorMessage(error);
      });

    // 立場マスターを取得
    await PositionApi.fetchPositionsList()
      .then(response => {
        if (response.result === true) {
          stateValue.positionsList = response.data.entities.positions;
        } else {
          stateValue.informationModal.show = true;
          stateValue.informationModal.message = extractErrorMessage(response);
        }
      })
      .catch(error => {
        stateValue.informationModal.show = true;
        stateValue.informationModal.message = extractErrorMessage(error);
      });

    // 現場に登録中の作業員のリストを取得する
    await FieldTreeWorkersApi.getAssignedWorker(
      this.props.dialog.state.field_tree_id,
      FieldTreeWorkersService.createSearchAssignedWorkerParam(
        stateValue.fetchAssignedWorkerParam
      )
    )
      .then(response => {
        if (response.result === true) {
          // 実際に画面に表示したり、値を送信するデータ
          stateValue.assignedWorkerList = _.cloneDeep(
            response.data.entities.assigned_workers
          );
          stateValue.assignedWorkerList.map(it => (it.isUpdate = false));

          // 変更されたかどうかをチェックするためのデータ
          stateValue.verifyAssignedWorkerList = _.cloneDeep(
            response.data.entities.assigned_workers
          );
        } else {
          const isDeletedFieldTree = isDeletedFieldTreeError(_.get(response, 'data.message'), this.props.dialog.state.field_tree_id);
          stateValue.informationModal.show = true;
          stateValue.informationModal.message = isDeletedFieldTree
            ? messageUtility.getMessage('E-000-075') : extractErrorMessage(response);
          stateValue.isDeletedFieldTree = isDeletedFieldTree;
        }
      })
      .catch(error => {
        stateValue.informationModal.show = true;
        stateValue.informationModal.message = extractErrorMessage(error);
      });

    this.setState(stateValue, this.validate);
  }

  /**
   * ダイアログを閉じる
   * */
  closeDialog = () => {
    const stateValue = _.cloneDeep(this.state);
    if (stateValue.isDeletedFieldTree) {
      window.location.reload();
    }
    stateValue.informationModal.show = false;
    stateValue.informationModal.message = '';
    this.setState(stateValue);
  };

  /**
   * 立場を選択
   * @param {number}positionCode: 立場コード
   * @param {number}index: 配列のindex番号
   * */
  changePosition = (positionCode, index) => {
    const stateValue = _.cloneDeep(this.state);
    stateValue.assignedWorkerList[
      index
    ].field_tree_worker_duty.const_org_position_cd = positionCode;
    this.setState(stateValue, () => this.validInitData(index));
  };

  /**
   * 職種を選択
   * @param {number}selectedLargeClassCode: 職種大分類コード
   * @param {number}selectedSmallClassCode: 職種小分類コード
   * @param {number}selectedWorkerJobId: 職種ID
   * @param {number}index: 配列のindex番号
   * */
  changeJobCategory = (
    selectedLargeClassCode,
    selectedSmallClassCode,
    selectedWorkerJobId,
    index
  ) => {
    const stateValue = _.cloneDeep(this.state);
    stateValue.assignedWorkerList[
      index
    ].field_tree_worker_duty.syokusyu_large_class_code = selectedLargeClassCode;
    stateValue.assignedWorkerList[
      index
    ].field_tree_worker_duty.syokusyu_small_class_code = selectedSmallClassCode;
    stateValue.assignedWorkerList[
      index
    ].field_tree_worker_duty.worker_job_id = selectedWorkerJobId;
    let selectedOtherJobName = null;
    let findResult = null;
    _.each(this.state.assignedWorkerList, fieldTreeWorker => {
      findResult = _.find(fieldTreeWorker.other_job_names, {
        worker_job_id: selectedWorkerJobId,
      });
      if (findResult) {
        selectedOtherJobName = findResult.other_job_name;
      }
    });
    stateValue.assignedWorkerList[
      index
    ].field_tree_worker_duty.other_job_name = selectedOtherJobName;

    this.setState(stateValue, () => this.validInitData(index));
  };

  setOtherJobName = (otherJobName, index) => {
    const stateValue = _.cloneDeep(this.state);

    if (isNullOrUndefinedOrEmptyStr(otherJobName)) {
      stateValue.assignedWorkerList[
        index
      ].field_tree_worker_duty.other_job_name = null;
    } else {
      stateValue.assignedWorkerList[
        index
      ].field_tree_worker_duty.other_job_name = otherJobName;
    }
    this.setState(stateValue, () => this.validInitData(index));
  };

  /**
   * 作業内容を変更する
   * @param {string}workContent: 入力された作業内容
   * @param {number}index: 配列のindex番号
   * */
  setWorkContent = (workContent, index) => {
    const stateValue = _.cloneDeep(this.state);

    if (isNullOrUndefinedOrEmptyStr(workContent)) {
      stateValue.assignedWorkerList[
        index
      ].field_tree_worker_duty.work_content = null;
    } else {
      stateValue.assignedWorkerList[
        index
      ].field_tree_worker_duty.work_content = workContent;
    }
    this.setState(stateValue, () => this.validInitData(index));
  };

  /**
   * 初期値と変更があったかを確認する
   * @param {number}index: 配列のindex番号
   * */
  validInitData = index => {
    const stateValue = _.cloneDeep(this.state);

    // 立場が変更されているか確認する
    stateValue.assignedWorkerList[index].isUpdate =
      stateValue.assignedWorkerList[index].field_tree_worker_duty
        .const_org_position_cd !==
      stateValue.verifyAssignedWorkerList[index].field_tree_worker_duty
        .const_org_position_cd;

    // 職種が変更されているか確認する
    if (stateValue.assignedWorkerList[index].isUpdate === false) {
      stateValue.assignedWorkerList[index].isUpdate = !(
        stateValue.assignedWorkerList[index].field_tree_worker_duty
          .syokusyu_large_class_code ===
          stateValue.verifyAssignedWorkerList[index].field_tree_worker_duty
            .syokusyu_large_class_code &&
        stateValue.assignedWorkerList[index].field_tree_worker_duty
          .syokusyu_small_class_code ===
          stateValue.verifyAssignedWorkerList[index].field_tree_worker_duty
            .syokusyu_small_class_code &&
        stateValue.assignedWorkerList[index].field_tree_worker_duty
          .worker_job_id ===
          stateValue.verifyAssignedWorkerList[index].field_tree_worker_duty
            .worker_job_id &&
        stateValue.assignedWorkerList[index].field_tree_worker_duty
          .other_job_name ===
          stateValue.verifyAssignedWorkerList[index].field_tree_worker_duty
            .other_job_name
      );
    }

    // 作業内容が変更されているか確認する
    if (stateValue.assignedWorkerList[index].isUpdate === false) {
      stateValue.assignedWorkerList[index].isUpdate =
        stateValue.assignedWorkerList[index].field_tree_worker_duty
          .work_content !==
        stateValue.verifyAssignedWorkerList[index].field_tree_worker_duty
          .work_content;
    }

    this.setState(stateValue, this.validate);
  };

  /**
   * ソート
   * @param {string}sortName:ソートしたい名前
   * */
  changeSort = sortName => {
    const fetchAssignedWorkerParam = _.cloneDeep(
      this.state.fetchAssignedWorkerParam
    );
    if (fetchAssignedWorkerParam.sortType === SORT_TYPE.asc.id) {
      fetchAssignedWorkerParam.sortType = SORT_TYPE.desc.id;
    } else {
      fetchAssignedWorkerParam.sortType = SORT_TYPE.asc.id;
    }

    if (fetchAssignedWorkerParam.order !== sortName) {
      fetchAssignedWorkerParam.order = sortName;
      fetchAssignedWorkerParam.sortType = SORT_TYPE.asc.id;
    }
    this.loadAssignedWorkerList(fetchAssignedWorkerParam);
  };

  /**
   * 現場に登録中の作業員のリストを取得する
   * */
  loadAssignedWorkerList = async fetchAssignedWorkerParam => {
    const stateValue = _.cloneDeep(this.state);
    stateValue.fetchAssignedWorkerParam = fetchAssignedWorkerParam;
    await FieldTreeWorkersApi.getAssignedWorker(
      this.props.dialog.state.field_tree_id,
      FieldTreeWorkersService.createSearchAssignedWorkerParam(
        stateValue.fetchAssignedWorkerParam
      )
    )
      .then(response => {
        if (response.result === true) {
          // 実際に画面に表示したり、値を送信するデータ
          stateValue.assignedWorkerList = _.cloneDeep(
            response.data.entities.assigned_workers
          );
          stateValue.assignedWorkerList.map(it => (it.isUpdate = false));

          // 変更されたかどうかをチェックするためのデータ
          stateValue.verifyAssignedWorkerList = _.cloneDeep(
            response.data.entities.assigned_workers
          );
        } else {
          stateValue.informationModal.show = true;
          stateValue.informationModal.message = extractErrorMessage(response);
        }
      })
      .catch(error => {
        stateValue.informationModal.show = true;
        stateValue.informationModal.message = extractErrorMessage(error);
      });
    this.setState(stateValue, this.validate);
  };

  /**
   * 作業員の職種・立場を登録する
   * */
  update = async () => {
    const stateValue = _.cloneDeep(this.state);

    // 作業員情報にエラーが存在する場合
    if (this.hasError(stateValue.assignedWorkerList)) {
      return;
    }

    const filterAssignedWorkerList = _.filter(
      stateValue.assignedWorkerList,
      it => {
        return it.isUpdate === true;
      }
    );

    // 更新するデータがなければダイアログを閉じる
    if (filterAssignedWorkerList.length < 1) {
      this.props.dialog.callback.close();
      return;
    }

    const updateAssignedWorkerList = FieldTreeWorkersService.createUpdateJobAndPositionParam(
      filterAssignedWorkerList
    );
    await FieldTreeWorkersApi.updateJobAndPosition(
      this.props.dialog.state.field_id,
      this.props.dialog.state.field_tree_id,
      updateAssignedWorkerList
    )
      .then(response => {
        if (response.result === true) {
          this.props.dialog.callback.close();
        } else {
          stateValue.informationModal.show = true;
          stateValue.informationModal.message = extractErrorMessage(response);
          this.setState(stateValue);
        }
      })
      .catch(error => {
        stateValue.informationModal.show = true;
        stateValue.informationModal.message = extractErrorMessage(error);
        this.setState(stateValue);
      });
  };

  /**
   * 作業員情報のバリデーションチェック
   * */
  validate = () => {
    const stateValue = _.cloneDeep(this.state);
    stateValue.assignedWorkerList.map(it => {
      const valid = validationByJoi(this.scheme, it.field_tree_worker_duty);
      if (valid.status === false) {
        it.errorList = valid.messages;
      } else {
        delete it.errorList;
      }
    });
    this.setState(stateValue);
  };

  /**
   * 作業員情報のエラー存在チェック
   * @param assignedWorkerList 対象の作業員
   * */
  hasError = assignedWorkerList => {
    let error = false;
    for (let idx in assignedWorkerList) {
      if (assignedWorkerList[idx].errorList) {
        error = true;
        break;
      }
    }
    return error;
  };

  /**
   * ドロップダウン選択時にスクロール位置を指定する
   * @param {object} targetPosition 選択したドロップダウンの位置情報
   * @param {number} basePosition 当該ドロップダウンの起点
   */
  jumpToShowDropdown = (targetPosition, basePosition) => {
    const scrollFrame = this.scrollFrameRef.current;
    const tablePosition = scrollFrame.getBoundingClientRect();
    scrollFrame.scrollBy(0, targetPosition.y - tablePosition.y - basePosition);
  };

  // 画面表示
  render() {
    return (
      <React.Fragment>
        <div id='dialog' className={Styles.dialog}>
          <div className='dialog-md dialog--modal'>
            <div className='dialog-md__header'>
              <span className='dialog__title'>{data.title}</span>
            </div>

            <div className='dialog-md__body'>
              <div className='dialog__item'>
                <p className='dialog__item-text'>
                  職種や立場、作業内容は労務安全の作業員名簿やCCUSへの施工体制情報の送信で利用されます。
                  <br />
                  現場代理人と作業主任者については労務安全の作業員名簿画面から設定してください。
                </p>
                <div
                  className='table__wrapper'
                  id='job-and-position-scroll-frame'
                  ref={this.scrollFrameRef}
                >
                  <table className='table'>
                    <thead className='table__header'>
                      <tr className='table__label'>
                        {data.tableTitles.map((title, index) => (
                          <th
                            className='value-table value-table--table__label'
                            key={index}
                            onClick={() => {
                              if (title.name === data.tableTitles[0].name) {
                                this.changeSort(title.name);
                              }
                            }}
                          >
                            <div className='value-table__label'>
                              {title.label}
                              {this.state.fetchAssignedWorkerParam.order ===
                                title.name &&
                                this.state.fetchAssignedWorkerParam.sortType ===
                                  SORT_TYPE.asc.id && (
                                  <button className='btn-icon--xs--dark'>
                                    <i className='i-sortaz i-1x' />
                                  </button>
                                )}
                              {this.state.fetchAssignedWorkerParam.order ===
                                title.name &&
                                this.state.fetchAssignedWorkerParam.sortType ===
                                  SORT_TYPE.desc.id && (
                                  <button className='btn-icon--xs--dark'>
                                    <i className='i-sortza i-1x' />
                                  </button>
                                )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className='table__body'>
                      {this.state.assignedWorkerList.length < 1 && (
                        <tr>
                          <td
                            className='no-assigned-worker'
                            colSpan={data.tableTitles.length}
                          >
                            {data.table.noAssignedWorker}
                          </td>
                        </tr>
                      )}
                      {this.state.assignedWorkerList.map(
                        (fieldTreeWorker, index) => (
                          <tr className='table__item' key={index}>
                            <td className='gate-list-value-table__name'>
                              <p>{fieldTreeWorker.fullname}</p>
                            </td>
                            <td className='gate-list-value-table__name'>
                              <div className='job-category'>
                                <div className='item-list'>
                                  <div className='item'>
                                    <JobCategoryDropdown
                                      title={data.table.title.jobCategory}
                                      placeholder={
                                        data.table.placeholder.jobCategory
                                      }
                                      large_code={
                                        fieldTreeWorker.field_tree_worker_duty
                                          .syokusyu_large_class_code
                                      }
                                      small_code={
                                        fieldTreeWorker.field_tree_worker_duty
                                          .syokusyu_small_class_code
                                      }
                                      worker_job_id={
                                        fieldTreeWorker.field_tree_worker_duty
                                          .worker_job_id
                                      }
                                      other_job_names={
                                        fieldTreeWorker.other_job_names
                                      }
                                      jobCategoriesList={
                                        this.state.jobCategoriesList
                                      }
                                      setIdentifier={index}
                                      set={this.changeJobCategory}
                                      jumpToShowDropdown={
                                        this.jumpToShowDropdown
                                      }
                                    />
                                  </div>
                                  {this.state.assignedWorkerList[index]
                                    .field_tree_worker_duty
                                    .syokusyu_large_class_code ===
                                    CCUS_JOB_CODE.SONOTA_SONOTA
                                      .LARGE_CLASS_CODE &&
                                    this.state.assignedWorkerList[index]
                                      .field_tree_worker_duty
                                      .syokusyu_small_class_code ===
                                      CCUS_JOB_CODE.SONOTA_SONOTA
                                        .SMALL_CLASS_CODE && (
                                      <div className='item'>
                                        <input
                                          type='text'
                                          name='otherJobName'
                                          value={
                                            fieldTreeWorker
                                              .field_tree_worker_duty
                                              .other_job_name || ''
                                          }
                                          maxLength={255}
                                          onChange={event =>
                                            this.setOtherJobName(
                                              event.target.value,
                                              index
                                            )
                                          }
                                          className='other-job-name'
                                        />
                                      </div>
                                    )}
                                </div>
                              </div>
                              <div className='position'>
                                <PositionDropdown
                                  title={data.table.title.position}
                                  placeholder={data.table.placeholder.position}
                                  value={
                                    fieldTreeWorker.field_tree_worker_duty
                                      .const_org_position_cd
                                  }
                                  options={this.state.positionsList}
                                  setIdentifier={index}
                                  set={this.changePosition}
                                  isOptional={true}
                                  jumpToShowDropdown={this.jumpToShowDropdown}
                                />
                              </div>
                            </td>
                            <td
                              className={`gate-list-value-table__name ${
                                fieldTreeWorker.errorList &&
                                fieldTreeWorker.errorList.work_content
                                  ? 'has-error'
                                  : ''
                              }`}
                            >
                              <textarea
                                className='work-content'
                                value={
                                  fieldTreeWorker.field_tree_worker_duty
                                    .work_content || ''
                                }
                                maxLength={100}
                                placeholder={data.table.placeholder.workContent}
                                onChange={event =>
                                  this.setWorkContent(event.target.value, index)
                                }
                              />
                              {fieldTreeWorker.errorList &&
                                fieldTreeWorker.errorList.work_content && (
                                  <div className='error-message'>
                                    {fieldTreeWorker.errorList.work_content}
                                  </div>
                                )}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                  {/* ドロップダウン選択時のスクロール位置を維持するため、スクロール枠の最大高から要素一つ分の高さを引いた値以上のブランクを入れる */}
                  <div className='blank-space' />
                </div>
              </div>
            </div>

            <div className='dialog-md__footer-horizontal'>
              <button
                className='btn--sub'
                onClick={() => this.props.dialog.callback.back()}
              >
                {data.button.back}
              </button>
              <button
                className='btn--effective'
                type='submit'
                onClick={() => this.update()}
              >
                {data.button.update}
              </button>
            </div>
          </div>
        </div>

        <Information
          title={this.state.isDeletedFieldTree ? messageUtility.getMessage('E-000-076') : ''}
          visible={this.state.informationModal.show}
          buttonText={data.button.close}
          message={this.state.informationModal.message}
          onClose={() => this.closeDialog()}
        />
      </React.Fragment>
    );
  }
}

// 定数
const data = {
  title: '作業員の職種・立場登録',
  tableTitles: [
    { name: 'name', label: '作業員名' },
    { name: 'job_and_category', label: '職種/立場' },
    { name: 'work_content', label: '作業内容' },
  ],
  // tableTitles: ['作業員名', '職種/立場', '作業内容'],
  table: {
    noAssignedWorker: '登録済み作業員がいません',
    title: {
      jobCategory: '職種',
      position: '立場',
    },
    placeholder: {
      jobCategory: '職種を選択',
      position: '立場を選択',
      workContent: '最大100文字で入力してください',
    },
  },
  button: {
    update: '更新',
    back: '戻る',
    close: '閉じる',
  },
};



// WEBPACK FOOTER //
// ./src/components/FieldTreeJobAndPositionRegisterDialog.jsx